import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * Cart recommendations. Reads the last added cart item, then fetches the
 * render-only `cart-recommendations-products` section via the Product
 * Recommendations API (cards are rendered in Liquid — single source of truth)
 * and morphs the returned slides into the carousel track.
 *
 * Loads on connect and re-fetches only when the cart's line items change — an
 * add (CartAddEvent) or a remove (a cart:update whose line count dropped). Pure
 * quantity changes don't alter which products are in the cart, so they're
 * ignored. Cards are morphed by slide id, so unchanged recommendations keep
 * their selection state.
 */
class CartRecommendations extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {Array<Record<string, any>>} Line items from the last /cart.js read */
  #cartItems = [];

  /** @type {number | null} Distinct line-item count at the last load */
  #lineCount = null;

  connectedCallback() {
    super.connectedCallback();
    this.#load();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeFetch?.abort();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /** Reload on add or remove; ignore pure quantity changes. */
  #handleCartUpdate = (event) => {
    if (event instanceof CartAddEvent) {
      this.#load(event.detail?.resource);
      return;
    }
    const items = event.detail?.resource?.items;
    if (!Array.isArray(items)) return;
    const removed = this.#lineCount != null && items.length < this.#lineCount;
    this.#lineCount = items.length;
    if (removed) this.#load(event.detail?.resource);
  };

  get #track() {
    return this.querySelector('.cart-recs-carousel__track');
  }

  /**
   * The most recently added cart line item, by the `__addToCartTime` line-item
   * property stamped on every add (assets/line-item-properties.js / stampAddToCartTime) —
   * /cart.js array order is not a reliable "newest last". Falls back to the last
   * array item when no timestamps are present.
   * @returns {Record<string, any> | null}
   */
  get #newestItem() {
    const items = this.#cartItems;
    if (!items.length) return null;
    let newest = items[items.length - 1];
    let newestTime = newest.properties?.__addToCartTime || '';
    for (const item of items) {
      const time = item.properties?.__addToCartTime || '';
      if (time > newestTime) {
        newest = item;
        newestTime = time;
      }
    }
    return newest;
  }

  /**
   * Every product type in the cart mapped to its chosen size, so a same-type
   * recommendation card can have its size pre-selected. All cart items are
   * considered; later items win when a type repeats (most recently chosen size).
   * @returns {Map<string, string>}
   */
  get #sizeByType() {
    const sizeByType = new Map();
    for (const item of this.#cartItems) {
      const type = (item.product_type || '').trim();
      const sizeOption = item.options_with_values?.find((o) => (o.name || '').toLowerCase() === 'size');
      const size = (sizeOption?.value || item.variant_options?.[0] || '').trim();
      if (type && size) sizeByType.set(type, size);
    }
    return sizeByType;
  }

  /** @param {Record<string, any>} [prefetchedCart] - A cart already read this tick (e.g. off a CartAddEvent), to skip re-fetching /cart.js. */
  async #load(prefetchedCart) {
    const { url, section, intent = 'related', limit = '10' } = this.dataset;
    if (!url || !section) return;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    const { signal } = this.#activeFetch;

    try {
      let cart = prefetchedCart;
      if (typeof cart?.token !== 'string') {
        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' }, signal });
        cart = cartResponse.ok ? await cartResponse.json() : null;
      }
      const items = cart?.items || [];
      this.#cartItems = items;
      this.#lineCount = items.length;
      const productId = this.#newestItem?.product_id;
      if (!productId) return this.#clear();

      const recUrl = `${url}?product_id=${productId}&section_id=${section}&intent=${intent}&limit=${limit}`;
      const response = await fetch(recUrl, { signal });
      if (!response.ok) return this.#clear();

      const track = this.#track;
      if (!track) return this.#clear();

      const doc = new DOMParser().parseFromString((await response.text()).trim(), 'text/html');
      const slides = doc.querySelectorAll('.cart-recs-carousel__slide');
      if (!slides.length) return this.#clear();

      // Morph the fetched (Liquid-rendered) slides into the track in place so
      // unchanged cards — matched by slide id — keep their DOM/component state.
      const newTrack = track.cloneNode(false);
      newTrack.append(...slides);
      morph(track, newTrack, MORPH_OPTIONS);
      // A reload shows a fresh set — start the carousel back at the beginning.
      track.scrollLeft = 0;

      this.classList.remove('hidden');
      this.#preselectMatchingSizes();
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') this.#clear();
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Pre-select the size on recommendation cards whose product type matches a
   * type already in the cart, so a same-type upsell is one click to add.
   */
  #preselectMatchingSizes() {
    const sizeByType = this.#sizeByType;
    if (!sizeByType.size) return;
    const track = this.#track;
    if (!track) return;

    for (const card of track.querySelectorAll('mini-product-card')) {
      const type = (card.dataset.productType || '').trim();
      const size = sizeByType.get(type);
      if (!size) continue;

      const select = card.querySelector('select');
      if (!(select instanceof HTMLSelectElement)) continue;

      const match = Array.from(select.options).find(
        (option) => option.value && !option.disabled && (option.dataset.option1 || '').trim() === size,
      );
      // Skip if unavailable or already on the target size (avoids re-firing
      // change on every cart:update when nothing actually changed).
      if (!match || select.value === match.value) continue;

      select.value = match.value;
      // Fire change so the card updates price, enables add, clears any error.
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #clear() {
    this.classList.add('hidden');
    this.#track?.replaceChildren();
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
